/**
 * src/components/agent/ChatHeader.jsx — Unified chat header.
 *
 * Phase 82-04 — Chat System Unification.
 *
 * Slots inside AgentShell's header area:
 *   - Back chevron (mobile only — forwarded by parent via onBack)
 *   - Editable title (contentEditable with Enter-commit / Escape-revert)
 *   - Assign-to-project quick action (opens ScopeChipPicker pinned to project tab)
 *   - Archive / Restore toggle
 *   - ScopeChipRow (add/remove chips, 4 types, no port)
 *
 * Hooks at top of body (Phase 90-07 rule).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, Archive as ArchiveIcon, ArchiveRestore, FolderPlus, RefreshCw } from 'lucide-react';
import ScopeChipRow from './ScopeChipRow';
import ScopeChipPicker from './ScopeChipPicker';
import { useT } from '../../lib/i18n/I18nContext';
import { useToast } from '../shared/ToastProvider';
import { syncGmailLeads } from '../../lib/api/leads.js';
import {
  rootStyle,
  topRowStyle,
  titleWrapStyle,
  titleTextStyle,
  titleEditingStyle,
  iconBtnStyle,
  iconBtnActiveStyle,
} from './ChatHeader.styles';

const TITLE_MAX = 120;

export default function ChatHeader({
  chat,
  onBack,
  showBack = false,
}) {
  const { t } = useT();
  const { showSuccess, showError } = useToast();
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const titleRef = useRef(null);
  const assignBtnRef = useRef(null);

  const title = chat?.title || 'New chat';
  const archived = Boolean(chat?.archivedAt);
  const scope = Array.isArray(chat?.scope) ? chat.scope : [];

  useEffect(() => {
    if (editing && titleRef.current) {
      titleRef.current.focus();
      const sel = window.getSelection?.();
      const range = document.createRange();
      range.selectNodeContents(titleRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    setDraftTitle(title);
    setEditing(true);
  }, [title]);

  const commitEdit = useCallback(async () => {
    const raw = titleRef.current?.textContent || '';
    const next = raw.trim().slice(0, TITLE_MAX);
    setEditing(false);
    if (!next || next === title) return;
    if (typeof chat?.rename === 'function') {
      try { await chat.rename(next); }
      catch (err) { console.warn('[ChatHeader] rename failed:', err?.message); }
    }
  }, [chat, title]);

  const cancelEdit = useCallback(() => {
    if (titleRef.current) titleRef.current.textContent = title;
    setEditing(false);
  }, [title]);

  const onTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }, [commitEdit, cancelEdit]);

  const onToggleArchive = useCallback(async () => {
    if (!chat) return;
    try {
      if (archived && typeof chat.restore === 'function') await chat.restore();
      else if (!archived && typeof chat.archive === 'function') await chat.archive();
    } catch (err) {
      console.warn('[ChatHeader] archive toggle failed:', err?.message);
    }
  }, [chat, archived]);

  const onSyncLeads = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await syncGmailLeads({});
      if (res?.ok) {
        showSuccess(t('agentChat.syncLeadsResult', {
          created: res.created ?? 0,
          skipped: res.skipped ?? 0,
        }));
      } else {
        showError(t('agentChat.syncLeadsError'));
      }
    } catch (err) {
      console.warn('[ChatHeader] lead sync failed:', err?.message);
      showError(t('agentChat.syncLeadsError'));
    } finally {
      setSyncing(false);
    }
  }, [syncing, showSuccess, showError, t]);

  const onAddScope = useCallback((entry) => {
    if (!entry || entry.type === 'port') return;
    if (typeof chat?.addScopeEntry === 'function') chat.addScopeEntry(entry);
  }, [chat]);

  const onRemoveScope = useCallback((type, id) => {
    if (typeof chat?.removeScopeEntry === 'function') chat.removeScopeEntry(type, id);
  }, [chat]);

  const onAssignProject = useCallback((entry) => {
    if (!entry || entry.type !== 'project') return;
    if (typeof chat?.setScope === 'function') {
      chat.setScope(prev => {
        const others = (prev || []).filter(e => e.type !== 'project');
        return [entry, ...others];
      });
    }
    setAssignOpen(false);
  }, [chat]);

  return (
    <div style={rootStyle} data-testid="chat-header">
      <div style={topRowStyle}>
        {showBack && (
          <button
            type="button"
            onClick={onBack}
            style={iconBtnStyle}
            aria-label={t('agentChat.backToList')}
            data-testid="chat-header-back"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        <div style={titleWrapStyle}>
          {editing ? (
            <div
              ref={titleRef}
              contentEditable
              suppressContentEditableWarning
              onKeyDown={onTitleKeyDown}
              onBlur={commitEdit}
              style={titleEditingStyle}
              role="textbox"
              aria-label="Chat title"
              data-testid="chat-header-title-edit"
            >
              {draftTitle}
            </div>
          ) : (
            <button
              type="button"
              onClick={startEdit}
              style={{
                ...titleTextStyle,
                border: 'none',
                cursor: 'pointer',
                background: 'transparent',
                textAlign: 'left',
              }}
              data-testid="chat-header-title"
              aria-label={`Rename chat "${title}"`}
            >
              {title}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onSyncLeads}
          disabled={syncing}
          style={syncing
            ? { ...iconBtnActiveStyle, cursor: 'default', opacity: 0.5 }
            : iconBtnStyle}
          aria-label={syncing ? t('agentChat.syncLeadsRunning') : t('agentChat.syncLeads')}
          aria-busy={syncing}
          data-testid="chat-header-sync-leads"
          title={syncing ? t('agentChat.syncLeadsRunning') : t('agentChat.syncLeads')}
        >
          <RefreshCw
            size={18}
            aria-hidden="true"
            style={syncing ? { animation: 'acn-spin 900ms linear infinite' } : undefined}
          />
          <style>{'@keyframes acn-spin { to { transform: rotate(360deg); } } @media (prefers-reduced-motion: reduce) { [data-testid="chat-header-sync-leads"] svg { animation: none !important; } }'}</style>
        </button>

        <div style={{ position: 'relative' }}>
          <button
            ref={assignBtnRef}
            type="button"
            onClick={() => setAssignOpen(o => !o)}
            style={assignOpen ? iconBtnActiveStyle : iconBtnStyle}
            aria-label={t('agentChat.assignToProject')}
            aria-expanded={assignOpen}
            data-testid="chat-header-assign"
            title={t('agentChat.assignToProject')}
          >
            <FolderPlus size={18} />
          </button>
          {assignOpen && (
            <ScopeChipPicker
              onAdd={onAssignProject}
              onClose={() => setAssignOpen(false)}
              existing={scope}
              allowedTypes={['project']}
            />
          )}
        </div>

        <button
          type="button"
          onClick={onToggleArchive}
          style={archived ? iconBtnActiveStyle : iconBtnStyle}
          aria-label={archived ? 'Restore chat' : 'Archive chat'}
          data-testid="chat-header-archive"
          title={archived ? 'Restore' : 'Archive'}
        >
          {archived ? <ArchiveRestore size={18} /> : <ArchiveIcon size={18} />}
        </button>
      </div>

      <ScopeChipRow
        scope={scope}
        onAdd={onAddScope}
        onRemove={onRemoveScope}
      />
    </div>
  );
}
